import type { CandleTimeframePreviewResponse } from "../types/tradeManagementReport";
import type { AnalyticsWindowSnakeFields } from "./analyticsWindowParams";
import {
  REPORT_ANALYSIS_MAX_BARS,
  timeframeExceedsReportBarBudget,
} from "./candleTimeframeBarBudget";
import {
  reportCandleBudgetBlockedMessage,
  reportTimeframeFitsMessage,
} from "./reportTimeframeCopy";

export type CandleTimeframePreviewUi = {
  primary: string;
  secondary?: string;
  subtext?: string;
  isWarning: boolean;
  /** When true, generate must stay blocked until the user shortens the window or TF. */
  exceedsBudget?: boolean;
  isLoading?: boolean;
  isEstimate?: boolean;
};

function resolvedCandleCount(preview: CandleTimeframePreviewResponse): number | undefined {
  const count =
    preview.analysis_bar_count ?? preview.bars_expected ?? preview.resolved_analysis_bar_count;
  return typeof count === "number" && Number.isFinite(count) ? count : undefined;
}

/** Stale BE copy — excursion uses the report candle timeframe, not a separate 1m fetch. */
const STALE_EXCURSION_NOTE_PATTERN =
  /excursion and mae\/mfe math may use 1m bars when analysis runs at/i;

function excursionNoteSubtext(
  preview: CandleTimeframePreviewResponse | null | undefined,
  includeExcursionNote: boolean
): string | undefined {
  if (!includeExcursionNote || !preview) return undefined;
  const note = preview.excursion_note?.trim();
  if (!note || STALE_EXCURSION_NOTE_PATTERN.test(note)) return undefined;
  return note;
}

export function buildCandleTimeframePreviewUi(input: {
  preview: CandleTimeframePreviewResponse | null | undefined;
  requestedTimeframe: string;
  windowFields?: AnalyticsWindowSnakeFields;
  windowSummaryLabel?: string;
  loading?: boolean;
  fetchFailed?: boolean;
  /** MAE/MFE excursion copy applies to Exit Placement only. */
  includeExcursionNote?: boolean;
}): CandleTimeframePreviewUi | null {
  const {
    preview,
    requestedTimeframe,
    windowFields,
    windowSummaryLabel,
    loading,
    fetchFailed,
    includeExcursionNote = false,
  } = input;
  if (!requestedTimeframe) return null;

  const budget =
    windowFields &&
    timeframeExceedsReportBarBudget(
      requestedTimeframe,
      windowFields,
      windowSummaryLabel
    );

  if (loading) {
    return {
      primary: "Checking candle count for your date range…",
      isWarning: false,
      isLoading: true,
    };
  }

  const clientExceeds = Boolean(budget?.exceeds && budget.estimate);
  const windowLabel = budget?.estimate?.windowLabel;

  if (fetchFailed && !preview) {
    if (clientExceeds) {
      return {
        primary: reportCandleBudgetBlockedMessage(requestedTimeframe),
        isWarning: true,
        exceedsBudget: true,
        isEstimate: true,
      };
    }
    return {
      primary: "Could not estimate candles for this range.",
      isWarning: false,
    };
  }

  if (preview) {
    const candleCount = resolvedCandleCount(preview);
    const apiExceeds =
      Boolean(preview.exceeds_budget) ||
      (typeof candleCount === "number" && candleCount > REPORT_ANALYSIS_MAX_BARS);

    if (apiExceeds) {
      return {
        primary: reportCandleBudgetBlockedMessage(requestedTimeframe),
        subtext: excursionNoteSubtext(preview, includeExcursionNote),
        isWarning: true,
        exceedsBudget: true,
      };
    }

    return {
      primary: reportTimeframeFitsMessage(requestedTimeframe, windowLabel, candleCount),
      subtext: excursionNoteSubtext(preview, includeExcursionNote),
      isWarning: false,
      exceedsBudget: false,
    };
  }

  if (clientExceeds) {
    return {
      primary: reportCandleBudgetBlockedMessage(requestedTimeframe),
      isWarning: true,
      exceedsBudget: true,
      isEstimate: true,
    };
  }

  return null;
}

/** @deprecated Use buildCandleTimeframePreviewUi */
export function formatCandleTimeframePreviewUi(
  preview: CandleTimeframePreviewResponse | null | undefined,
  requestedTimeframe: string
): CandleTimeframePreviewUi | null {
  return buildCandleTimeframePreviewUi({ preview, requestedTimeframe });
}
